// Project department dashboard. Tiles/charts are sourced from GetProjectDashboardSummary
// (single aggregated row, CC-scoped server-side by RoleId/UserId) — see
// spGetProjectDashboardSummary.sql. Every metric here shares the same CC scope, mirroring the
// RoleId/UserId-scoped method already used for Receivables/Payables/Purchase on Top Level.
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Building2, HardHat, Copy, Loader2 } from 'lucide-react';
import FrequentAccessCard from './FrequentAccessCard';
import { formatCurrency } from '../../utilities/formatCurrency';
import {
    fetchProjectDashboardSummary,
    selectProjectDashboardSummary,
    selectProjectDashboardLoading,
} from '../../slices/dashboardSlice/projectDashboardSlice';

const cardClass = 'bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700';

const KPI_TONES = {
    blue:   { icon: 'bg-blue-100 dark:bg-blue-900/30',     text: 'text-blue-600 dark:text-blue-500' },
    orange: { icon: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-600 dark:text-orange-500' },
};

// Bar colour for a cost center's utilization %, worst first.
const utilizationBar = (pct) => {
    if (pct >= 95) return 'bg-red-600';
    if (pct >= 85) return 'bg-orange-500';
    return 'bg-yellow-500';
};

const KpiTile = ({ title, value, subtitle, icon: Icon, tone = 'blue', loading }) => {
    const t = KPI_TONES[tone] || KPI_TONES.blue;
    return (
        <div className={`${cardClass} p-4 flex items-center gap-4`}>
            <div className={`w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 ${t.icon}`}>
                <Icon className={`w-5 h-5 ${t.text}`} />
            </div>
            <div className="min-w-0">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{title}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">
                    {loading ? <Loader2 className={`w-5 h-5 animate-spin ${t.text}`} /> : (value ?? 0)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{subtitle}</p>
            </div>
        </div>
    );
};

// Horizontal bar row used in the "Expenses this month" card.
const ExpenseBar = ({ label, value, max, color }) => (
    <div className="flex items-center gap-3 text-xs">
        <span className="w-28 flex-shrink-0 text-gray-600 dark:text-gray-300">{label}</span>
        <div className="flex-1 h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
            <div className={`h-full rounded-full ${color}`} style={{ width: `${max > 0 ? Math.min((value / max) * 100, 100) : 0}%` }} />
        </div>
        <span className="w-28 text-right tabular-nums whitespace-nowrap font-medium text-gray-900 dark:text-white">₹{formatCurrency(value || 0)}</span>
    </div>
);

const ProjectDashboard = ({ onNavigate, linkFrequency, trackMenuUsage }) => {
    const { roleData, userData } = useSelector((state) => state.auth);
    const dispatch = useDispatch();

    const summary = useSelector(selectProjectDashboardSummary);
    const loading = useSelector(selectProjectDashboardLoading);

    const userId = userData?.userId || userData?.UID || userData?.employeeId || '';
    const roleId = userData?.roleId || userData?.RID || 0;

    useEffect(() => {
        // roleId/userId drive backend CC-scoping: for roles with ApplicableForCC='Yes',
        // results are restricted to the cost centers assigned to this user in UserCostCenters.
        dispatch(fetchProjectDashboardSummary({ roleId, userId }));
    }, [dispatch, roleId, userId]);

    const budgetAssigned = summary?.BudgetAssigned || 0;
    const budgetUtilized = summary?.BudgetUtilized || 0;
    const budgetBalance = summary?.BudgetBalance || 0;
    const utilizedPct = budgetAssigned > 0 ? Math.round((budgetUtilized / budgetAssigned) * 100) : 0;
    const leftPct = Math.max(100 - utilizedPct, 0);

    const expensesCurrent = summary?.ExpensesCurrentMonth || 0;
    const expensesPrevious = summary?.ExpensesPreviousMonth || 0;
    const expenseChange = expensesPrevious > 0 ? Math.round(((expensesCurrent - expensesPrevious) / expensesPrevious) * 100) : 0;
    // Cash voucher / vendor invoice split renders only once the summary SP returns it.
    const hasExpenseSplit = summary?.CashVoucherCurrentMonth != null && summary?.VendorInvoiceCurrentMonth != null;
    const expenseMax = Math.max(expensesCurrent, expensesPrevious);

    // Per-CC utilization list renders only once the summary SP returns it: [{ CCName, UtilizedPct }].
    const nearLimit = Array.isArray(summary?.CostCentersNearLimit)
        ? [...summary.CostCentersNearLimit].sort((a, b) => (b.UtilizedPct || 0) - (a.UtilizedPct || 0)).slice(0, 4)
        : null;

    const activeCCs = summary?.ActiveCostCenterCount ?? 0;
    const todayLabel = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' });

    // "View budget →" opens the role's own budget screen from its menu, tracked the same way
    // as a Quick access click. Hidden if the role has no budget screen.
    const budgetMenuItem = roleData?.menuItems?.find((item) => (item.SUBLI || '').toLowerCase().includes('budget'));
    const openBudget = () => {
        const item = budgetMenuItem;
        const name = item.SUBLI || item.FirmFunctionalAreaName || 'Unknown';
        const linkData = {
            id: item.FirmFunctionalAreaId,
            name,
            section: item.LI || 'Other',
            path: item.Path,
            type: item.Type,
            reactRoute: `${item.Type?.toLowerCase()}/${name.toLowerCase().replace(/\s+/g, '-')}`,
            originalItem: item,
            li: item.LI,
            subli: item.SUBLI,
            url: item.UL,
        };
        if (trackMenuUsage) trackMenuUsage(linkData);
        if (onNavigate) onNavigate(linkData.reactRoute, linkData);
    };

    return (
        <div className="space-y-4">
            {/* Welcome Header */}
            <div className="bg-gradient-to-r from-[#0d1b5e] to-[#0a1240] dark:from-[#0a1240] dark:to-gray-900 rounded-lg p-6 text-white transition-colors">
                <h1 className="text-2xl font-bold mb-2">Welcome Back!</h1>
                <p className="text-orange-200 dark:text-orange-300">Employee: {userData?.firstName} | Role: {userData?.roleCode}</p>
            </div>

            {/* Section header */}
            <div className="flex flex-wrap items-end justify-between gap-2">
                <div>
                    <p className="text-xs font-semibold tracking-wide text-gray-500 dark:text-gray-400">PROJECTS</p>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Project overview</h2>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    {todayLabel}{!loading && ` · ${activeCCs} active cost centers`}
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                <div className="lg:col-span-2 space-y-4">
                    {/* Budget utilization */}
                    <div className={`${cardClass} p-5`}>
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <h3 className="text-base font-semibold text-gray-900 dark:text-white">Budget utilization</h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Across all active cost centers</p>
                            </div>
                            {budgetMenuItem && (
                                <button onClick={openBudget} className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap">
                                    View budget →
                                </button>
                            )}
                        </div>

                        {loading ? (
                            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>
                        ) : (
                            <>
                                <div className="flex flex-wrap items-baseline gap-x-3 mt-3">
                                    <span className={`text-5xl font-bold ${utilizedPct >= 90 ? 'text-orange-700 dark:text-orange-500' : 'text-gray-900 dark:text-white'}`}>
                                        {utilizedPct}%
                                    </span>
                                    <span className="text-sm text-gray-600 dark:text-gray-300">
                                        of assigned budget used · <span className="font-semibold text-gray-900 dark:text-white">{leftPct}% left</span>
                                    </span>
                                </div>
                                <div className="flex h-3 w-full rounded-full overflow-hidden bg-green-100 dark:bg-green-900/30 mt-3">
                                    <div className="h-full bg-orange-500" style={{ width: `${Math.min(utilizedPct, 100)}%` }} />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Assigned</p>
                                        <p className="text-lg font-bold tabular-nums text-gray-900 dark:text-white">₹{formatCurrency(budgetAssigned)}</p>
                                    </div>
                                    <div>
                                        <p className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                                            <span className="w-2 h-2 rounded-full bg-orange-500 mr-1.5" />Utilized
                                        </p>
                                        <p className="text-lg font-bold tabular-nums text-gray-900 dark:text-white">₹{formatCurrency(budgetUtilized)}</p>
                                    </div>
                                    <div>
                                        <p className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                                            <span className="w-2 h-2 rounded-full bg-green-500 mr-1.5" />Balance
                                        </p>
                                        <p className="text-lg font-bold tabular-nums text-green-600 dark:text-green-400">₹{formatCurrency(budgetBalance)}</p>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* KPI tiles */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <KpiTile
                            title="Active cost centers"
                            value={summary?.ActiveCostCenterCount}
                            subtitle="Not closed or suspended"
                            icon={Building2}
                            loading={loading}
                        />
                        <KpiTile
                            title="Labour on site"
                            value={summary?.LabourCount}
                            subtitle="Approved, currently working"
                            icon={HardHat}
                            loading={loading}
                        />
                        <KpiTile
                            title="Open purchase orders"
                            value={summary?.OpenPOCount}
                            subtitle="Service provider + supplier"
                            icon={Copy}
                            tone="orange"
                            loading={loading}
                        />
                    </div>

                    {/* Expenses + Cost centers near limit */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
                        <div className={`${cardClass} p-5 h-full`}>
                            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Expenses this month</h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Cash vouchers + vendor invoices</p>

                            {loading ? (
                                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>
                            ) : (
                                <>
                                    <div className="flex flex-wrap items-center gap-3 mt-3 mb-4">
                                        <span className="text-3xl font-bold tabular-nums text-gray-900 dark:text-white">₹{formatCurrency(expensesCurrent)}</span>
                                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${expenseChange > 0
                                            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                            : expenseChange < 0
                                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                                            {expenseChange > 0 ? '+' : ''}{expenseChange}% vs last month
                                        </span>
                                    </div>
                                    <div className="space-y-3">
                                        {hasExpenseSplit ? (
                                            <>
                                                <ExpenseBar label="Cash vouchers" value={summary.CashVoucherCurrentMonth} max={expenseMax} color="bg-blue-400" />
                                                <ExpenseBar label="Vendor invoices" value={summary.VendorInvoiceCurrentMonth} max={expenseMax} color="bg-[#1e3a8a]" />
                                            </>
                                        ) : (
                                            <ExpenseBar label="This month" value={expensesCurrent} max={expenseMax} color="bg-[#1e3a8a]" />
                                        )}
                                        <ExpenseBar label="Last month total" value={expensesPrevious} max={expenseMax} color="bg-gray-400" />
                                    </div>
                                </>
                            )}
                        </div>

                        <div className={`${cardClass} p-5 h-full`}>
                            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Cost centers near budget limit</h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Highest utilization first</p>

                            {loading ? (
                                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>
                            ) : nearLimit && nearLimit.length > 0 ? (
                                <ul className="mt-3 divide-y divide-gray-100 dark:divide-gray-700 border-t border-gray-100 dark:border-gray-700">
                                    {nearLimit.map((cc) => {
                                        const pct = Math.round(cc.UtilizedPct || 0);
                                        return (
                                            <li key={cc.CCName} className="flex items-center gap-3 py-2.5 text-sm">
                                                <span className="flex-1 min-w-0 truncate font-medium text-gray-900 dark:text-white">{cc.CCName}</span>
                                                <div className="w-28 h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden flex-shrink-0">
                                                    <div className={`h-full rounded-full ${utilizationBar(pct)}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                                                </div>
                                                <span className={`w-12 text-right tabular-nums font-semibold ${pct >= 95 ? 'text-red-600 dark:text-red-400' : 'text-orange-600 dark:text-orange-400'}`}>
                                                    {pct}%
                                                </span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            ) : (
                                <p className="text-xs text-gray-400 dark:text-gray-500 py-8 text-center">No cost-center breakdown available</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Quick access — shared card, used as-is */}
                <div className="lg:col-span-1">
                    <FrequentAccessCard
                        roleData={roleData}
                        linkFrequency={linkFrequency}
                        trackMenuUsage={trackMenuUsage}
                        onNavigate={onNavigate}
                    />
                </div>
            </div>
        </div>
    );
};

export default ProjectDashboard;
